import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, PlugZap, X } from "lucide-react";
import { api, messageErreur } from "@/lib/api";
import { formatDateHeure } from "@/lib/format";

const Apercu = ({ data }) => (
  <pre className="at-soft p-3 text-xs whitespace-pre-wrap break-words mt-3 max-h-56 overflow-auto" style={{ fontFamily: "var(--font)" }} data-testid="connector-preview">
    {JSON.stringify(data, null, 2)}
  </pre>
);

const Carte = ({ c, onMaj }) => {
  const [cle, setCle] = useState("");
  const [resultat, setResultat] = useState(null);
  const [test, setTest] = useState(false);
  const connecte = c.mode === "reel";

  const tester = async () => {
    setTest(true);
    try { const { data } = await api.post(`/connexions/${c.code}/tester`); setResultat(data); toast.success(data.message); onMaj(); }
    catch (e) { toast.error(messageErreur(e)); } finally { setTest(false); }
  };
  const enregistrer = async (e) => {
    e.preventDefault();
    try { await api.put(`/connexions/${c.code}/cle`, { cle }); setCle(""); toast.success("Clé enregistrée (chiffrée côté serveur)."); onMaj(); }
    catch (err) { toast.error(messageErreur(err)); }
  };
  const retirer = async () => {
    try { await api.delete(`/connexions/${c.code}/cle`); toast.success("Clé retirée : retour en simulation."); onMaj(); }
    catch (err) { toast.error(messageErreur(err)); }
  };

  return (
    <div className="at-card p-5" data-testid={`connector-card-${c.code}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0"><p className="font-bold">{c.nom}</p><p className="text-sm at-muted mt-0.5">{c.description}</p></div>
        <span data-testid={`connector-status-${c.code}`} className="shrink-0 px-3 py-1 at-pill text-xs font-bold"
          style={{ background: connecte ? "color-mix(in srgb, var(--success) 15%, transparent)" : "var(--glow)", color: connecte ? "var(--success)" : "var(--ink)" }}>
          {c.statut}
        </span>
      </div>
      {c.dernier_test && <p className="text-xs at-muted mt-2">Dernier test : {formatDateHeure(c.dernier_test)}</p>}
      <form onSubmit={enregistrer} className="flex gap-2 mt-4">
        <input type="password" autoComplete="off" className="at-input" placeholder={connecte ? `Clé enregistrée ${c.cle_masquee}` : "Clé d'API (lot 7)"} value={cle} onChange={(e) => setCle(e.target.value)} data-testid={`connector-key-input-${c.code}`} />
        <button type="submit" disabled={!cle} className="at-icon-btn at-btn-ghost shrink-0" aria-label="Enregistrer la clé" data-testid={`connector-save-key-${c.code}`}><KeyRound size={17} /></button>
        {connecte && <button type="button" onClick={retirer} className="at-icon-btn shrink-0" aria-label="Retirer la clé" data-testid={`connector-remove-key-${c.code}`}><X size={17} /></button>}
      </form>
      <button type="button" onClick={tester} disabled={test} className="at-btn at-btn-primary w-full mt-3" data-testid={`connector-test-${c.code}`}><PlugZap size={17} />{test ? "Test en cours…" : "Tester"}</button>
      {resultat && <Apercu data={resultat.apercu} />}
    </div>
  );
};

const eur = (n) => `${n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 4 })} €`;

const CoutIA = () => {
  const [c, setC] = useState(null);
  useEffect(() => {
    const lire = () => api.get("/ia/couts").then((r) => setC(r.data)).catch(() => {});
    lire();
    const t = setInterval(lire, 5000);
    return () => clearInterval(t);
  }, []);
  if (!c) return null;
  return (
    <section className="at-card p-5 mb-4 space-y-3" data-testid="ai-cost-counter">
      <div className="flex items-baseline justify-between gap-2"><p className="font-bold">Coût IA en temps réel</p><span className="text-xs at-muted">Fictif (simulation)</span></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="at-soft p-3"><p className="at-label">Aujourd'hui</p><p className="text-xl font-bold" data-testid="ai-cost-day">{eur(c.jour)}</p><p className="text-xs at-muted">{c.appels_jour} appel{c.appels_jour > 1 ? "s" : ""}</p></div>
        <div className="at-soft p-3"><p className="at-label">Ce mois</p><p className="text-xl font-bold" data-testid="ai-cost-month">{eur(c.mois)}</p></div>
      </div>
      <ul className="space-y-1.5">
        {c.par_fonction.map((f) => (
          <li key={f.fonction} className="flex justify-between gap-2 text-sm" data-testid={`ai-cost-fn-${f.fonction}`}>
            <span className="min-w-0"><span className="font-semibold">{f.label}</span><span className="block text-xs at-muted">{f.modele} · {f.appels} appel{f.appels > 1 ? "s" : ""}</span></span>
            <span className="font-bold whitespace-nowrap">{eur(f.cout)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default function Connexions() {
  const [liste, setListe] = useState([]);
  const charger = useCallback(() => api.get("/connexions").then((r) => setListe(r.data)), []);
  useEffect(() => { charger(); }, [charger]);
  return (
    <div>
      <CoutIA />
      <h2 className="text-base md:text-lg font-bold">Connexions</h2>
      <p className="at-muted text-sm mt-1 mb-4">Sans clé, chaque connecteur fonctionne en simulation avec des données réalistes. Les vraies connexions seront branchées au lot 7.</p>
      <div className="grid lg:grid-cols-2 gap-3" data-testid="connectors-list">{liste.map((c) => <Carte key={c.code} c={c} onMaj={charger} />)}</div>
    </div>
  );
}
